import { useEffect, useId, useState } from "react";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Select } from "../../components/Select";
import type { CameraResponse, ReportAlertFilter, ReportFilters } from "../../lib/api";
import { isoToZonedInput, zonedInputToIso } from "../../lib/storeTime";
import { CameraPicker } from "./CameraPicker";
import styles from "./ReportsPanel.module.css";
import { ALERT_FILTERS, REPORT_CATEGORIES, REPORT_RANGES, type ReportRange } from "./reportFilters";

// A list value replaces every value of a repeated parameter (camera=…&camera=…).
export type ParamChanges = Record<string, string | string[] | null>;

type ReportFiltersBarProps = {
  filters: ReportFilters;
  range: ReportRange | null; // null = a custom window
  timeZone: string;
  cameras: CameraResponse[];
  onChange: (changes: ParamChanges) => void;
};

const ALERT_FILTER_LABEL: Record<ReportAlertFilter, string> = {
  sent: "Sent",
  suppressed: "Suppressed",
  none: "No alert",
};

export const ReportFiltersBar = ({
  filters,
  range,
  timeZone,
  cameras,
  onChange,
}: ReportFiltersBarProps) => {
  const id = useId();
  const onParam = (key: string, value: string | null) => onChange({ [key]: value });
  const [search, setSearch] = useState(filters.q);
  useEffect(() => setSearch(filters.q), [filters.q]);
  const [from, setFrom] = useState(() => isoToZonedInput(filters.from, timeZone));
  const [to, setTo] = useState(() => isoToZonedInput(filters.to, timeZone));
  useEffect(() => {
    setFrom(isoToZonedInput(filters.from, timeZone));
    setTo(isoToZonedInput(filters.to, timeZone));
  }, [filters.from, filters.to, timeZone]);
  const customValid = Boolean(from && to) && from < to;

  return (
    <div className={styles.filters}>
      <div className={styles.presets}>
        {(Object.keys(REPORT_RANGES) as ReportRange[]).map((r) => (
          <Button
            key={r}
            size="sm"
            variant={r === range ? "primary" : "outline"}
            aria-pressed={r === range}
            onClick={() => onChange({ range: r, from: null, to: null })}
          >
            {r}
          </Button>
        ))}
      </div>
      <div className={styles.field}>
        <label htmlFor={`${id}-from`}>From</label>
        <Input
          id={`${id}-from`}
          type="datetime-local"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
        />
      </div>
      <div className={styles.field}>
        <label htmlFor={`${id}-to`}>To</label>
        <Input
          id={`${id}-to`}
          type="datetime-local"
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
      </div>
      <Button
        size="sm"
        variant="secondary"
        disabled={!customValid}
        onClick={() =>
          onChange({
            range: null,
            from: zonedInputToIso(from, timeZone),
            to: zonedInputToIso(to, timeZone),
          })
        }
      >
        Apply
      </Button>
      <CameraPicker
        cameras={cameras}
        selected={filters.cameras}
        onChange={(ids) => onChange({ camera: ids })}
      />
      <div className={styles.field}>
        <label htmlFor={`${id}-severity`}>Severity</label>
        <Select
          id={`${id}-severity`}
          value={filters.minSeverity ?? ""}
          onChange={(e) => onParam("min_severity", e.target.value || null)}
        >
          <option value="">Any</option>
          {[5, 4, 3, 2, 1].map((n) => (
            <option key={n} value={n}>
              {n === 5 ? "5 only" : `${n} and up`}
            </option>
          ))}
        </Select>
      </div>
      <div className={styles.field}>
        <label htmlFor={`${id}-alert`}>Alert</label>
        <Select
          id={`${id}-alert`}
          value={filters.alert ?? ""}
          onChange={(e) => onParam("alert", e.target.value || null)}
        >
          <option value="">All</option>
          {ALERT_FILTERS.map((a) => (
            <option key={a} value={a}>
              {ALERT_FILTER_LABEL[a]}
            </option>
          ))}
        </Select>
      </div>
      <div className={styles.field}>
        <label htmlFor={`${id}-category`}>Category</label>
        <Select
          id={`${id}-category`}
          value={filters.category ?? ""}
          onChange={(e) => onParam("category", e.target.value || null)}
        >
          <option value="">All</option>
          {REPORT_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
      </div>
      <form
        className={styles.search}
        onSubmit={(e) => {
          e.preventDefault();
          onParam("q", search.trim() || null);
        }}
      >
        <Input
          type="search"
          aria-label="Search text"
          placeholder="Search text…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </form>
    </div>
  );
};
